import axios, { AxiosError, AxiosInstance } from 'axios';

export interface ServiceItem {
    id: number;
    image: string;
}



export interface DeleteGalleryImagesData {
    id: number;
    image: number[];
}

export interface Service {
    id: number;
    name: string;
    description: string;
    price: number;
    main_image: string;
    phone_number: string;
    created_at: string;
    updated_at: string;
    gallery_image: ServiceItem[];
    images: ServiceItem[];
}

export interface CreateServiceData {
    name?: string;
    description?: string;
    price?: number;
    main_image?: File;
    phone_number?: string;
    gallery_image?: File[]; // Matching Go backend's field name
}

export interface UpdateServiceData extends CreateServiceData {
    id: number;
}

class ServiceClass {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_API_URL,
            timeout: 10000,
        });

        this.axiosInstance.interceptors.request.use(
            (config) => {
                const storageType = localStorage.getItem('storageType');
                const storage = storageType === 'local' ? localStorage : sessionStorage;
                const token = storage.getItem('token');
                
                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );
    }

    async createService(data: CreateServiceData): Promise<Service | undefined> {
        try {
            const formData = new FormData();
            
            // Handle text fields
            if (data.name) formData.append('name', data.name);
            if (data.description) formData.append('description', data.description);
            if (data.price) formData.append('price', data.price.toString());
            if (data.phone_number) formData.append('phone_number', data.phone_number);

            // Handle main image
            if (data.main_image instanceof File) {
                formData.append('main_image', data.main_image);
            }

            // Handle gallery images
            if (data.gallery_image && data.gallery_image.length > 0) {
                data.gallery_image.forEach((file) => {
                    formData.append('gallery_image', file);
                });
            }

            const response = await this.axiosInstance.post<{
                message: string;
                service: Service;
            }>('/admin/service', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            console.log(response.data.service);
            return response.data.service;
        } catch (error) {
            this.handleError(error, 'Failed to create service');
            return undefined;
        }
    }

    async getAllServices(): Promise<Service[]> {
        try {
            const response = await this.axiosInstance.get<{
                data: Service[];
            }>('/service');
            console.log(response.data);
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve services');
            return [];
        }
    }

    async getServiceByID(id: number): Promise<Service | undefined> {
        try {
            const response = await this.axiosInstance.get<{
                data: Service;
            }>(`/service/${id}`);
            
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve service');
            return undefined;
        }
    }

    async getServiceByName(name: string): Promise<Service[]> {
        try {
            const response = await this.axiosInstance.get<{
                data: Service[];
            }>(`/service/get-name?name=${encodeURIComponent(name)}`);
            
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to search services by name');
            return [];
        }
    }

    async updateService(data: UpdateServiceData): Promise<Service | undefined> {
        try {
            const formData = new FormData();
            
            // Handle text fields
            if (data.name) formData.append('name', data.name);
            if (data.description) formData.append('description', data.description);
            if (data.price) formData.append('price', data.price.toString());
            if (data.phone_number) formData.append('phone_number', data.phone_number);

            // Handle main image
            if (data.main_image instanceof File) {
                formData.append('main_image', data.main_image);
            }

            // Handle gallery images
            if (data.gallery_image && data.gallery_image.length > 0) {
                data.gallery_image.forEach((file) => {
                    formData.append('gallery_image', file);
                });
            }

            const response = await this.axiosInstance.put<{
                message: string;
                data: Service;
            }>(`/admin/service/${data.id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to update service');
            return undefined;
        }
    }

    async deleteService(id: number): Promise<void> {
        try {
            await this.axiosInstance.delete(`/admin/service/${id}`);
        } catch (error) {
            this.handleError(error, 'Failed to delete service');
        }
    }

    private handleError(error: unknown, message: string) {
        if (error instanceof AxiosError) {
            const backendMsg = error.response?.data?.error || error.response?.data?.message || JSON.stringify(error.response?.data);
            console.error(`${message}:`, backendMsg);
            throw new Error(backendMsg || message);
        }
        console.error(message, error);
        throw new Error(message);
    }
    async deleteServiceGalleryImages(serviceId: number, imageIds: number[]): Promise<Service | undefined> {
        try {
            const response = await this.axiosInstance.put<{
                message: string;
                data: Service;
            }>(`/admin/service/${serviceId}?gallery-image-delete=${imageIds.join(',')}`, {}, {
                headers: { 'Content-Type': 'application/json' },
            });

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to delete gallery images');
            return undefined;
        }
    }

}

export const serviceClass = new ServiceClass();